import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Bell, User, Flame, ChevronDown, Zap, Crown, Target, CheckCircle, Users } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useUiStore } from '@/stores/ui-store';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useStreak } from '@/hooks/useStreak';
import { AnnouncementBanner } from './AnnouncementBanner';
import { TataPriceHikeCountdown } from '@/components/widgets/TataPriceHikeCountdown';
import { StreakMilestoneToast } from '@/components/StreakMilestoneToast';

const CommandPalette = lazy(() => import('@/components/ui/CommandPalette'));

export function Header() {
  const { user, logout, isAuthenticated } = useAuthStore();
  const { theme } = useUiStore();
  const navigate = useNavigate();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // T-76: the header now renders for logged-out readers too. Both of these
  // queries hit authenticated endpoints, so leaving them enabled would fire a
  // guaranteed 401 on every public page load, and the notifications one would
  // repeat it every 30 seconds for the whole visit. `enabled` is the frontend
  // equivalent of the undefined-userId trap that turned 401s into 500s on the
  // backend last time: loosening the gate is not enough, the callers matter.
  const { data: unreadCount } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api.notifications.unreadCount(),
    refetchInterval: 30000,
    enabled: isAuthenticated,
  });

  const { data: streak } = useQuery({
    queryKey: ['streak'],
    queryFn: () => api.users.getStreak(),
    enabled: isAuthenticated,
  });

  const streakState = useStreak();

  // Global keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/welcome');
    setIsProfileOpen(false);
  };

  return (
    <>
      <AnnouncementBanner />
      <TataPriceHikeCountdown />
      <header className="sticky top-0 z-50 border-b border-border-primary bg-bg-primary/95 backdrop-blur-lg">
        <div className="flex h-14 items-center justify-between px-4 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            {/* T-179: the wordmark was near-invisible — a 24px mark in a 56px
                bar, with the name itself hidden below the `sm` breakpoint, so
                on a phone the masthead was an unlabelled logo. The publication
                should say its own name at every width. */}
            <img src={theme === 'dark' ? '/logo-dark.png' : '/logo.png'} alt="MotoWalla" className="h-8" />
            <span className="text-xl font-bold text-text-primary tracking-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>MotoWalla</span>
          </Link>

          {/* Search Bar - triggers Command Palette */}
          <button
            onClick={() => setIsCommandPaletteOpen(true)}
            className="flex flex-1 max-w-xl items-center gap-3 rounded-xl border border-border-primary bg-bg-secondary/50 px-4 py-2 text-left transition-colors hover:border-primary-500/30 hover:bg-bg-secondary"
          >
            <Search className="h-4 w-4 text-text-tertiary" />
            <span className="flex-1 text-sm text-text-tertiary">
              Search brands, garages, posts...
            </span>
            <kbd className="hidden sm:flex items-center gap-0.5 rounded-md border border-border-secondary bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary">
              <span className="text-xs">⌘</span>K
            </kbd>
          </button>

          {/* Right side */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* UI-094: Streak — compact on mobile, full on desktop */}
            {/* T-179: this cluster was a flame, a bare number, an icon and a
                "2/3" with no label anywhere — unreadable unless you already
                knew what it meant. Icons are not labels. Give it an accessible
                name and a hover title; the visual stays compact. */}
            <div
              className="flex sm:hidden items-center gap-1 rounded-lg bg-orange-500/10 px-2 py-1 border border-orange-500/20"
              title={`${streakState.currentStreak}-day reading streak`}
              aria-label={`${streakState.currentStreak}-day reading streak`}
            >
              <Flame className="h-3.5 w-3.5 text-orange-500" />
              <span className="text-xs font-bold text-orange-500">{streakState.currentStreak}</span>
              {streakState.goalComplete && <CheckCircle className="h-3 w-3 text-green-500" />}
            </div>
            {/* Streak + Daily Goal — desktop */}
            <div
              className="hidden sm:flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 px-3 py-1.5 border border-orange-500/20"
              title={`${streakState.currentStreak}-day reading streak — ${streakState.dailyReads} of ${streakState.dailyGoal} articles read today`}
              aria-label={`${streakState.currentStreak}-day reading streak, ${streakState.dailyReads} of ${streakState.dailyGoal} articles read today`}
            >
              <Flame className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-bold text-orange-500">{streakState.currentStreak}</span>
              <div className="w-px h-4 bg-orange-500/20 mx-0.5" />
              {streakState.goalComplete ? (
                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Target className="h-3.5 w-3.5 text-orange-400/60" />
              )}
              <div className="w-12 h-1.5 rounded-full bg-orange-500/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    streakState.goalComplete
                      ? 'bg-gradient-to-r from-green-500 to-emerald-400'
                      : 'bg-gradient-to-r from-orange-500 to-red-400'
                  }`}
                  style={{ width: `${streakState.dailyProgress}%` }}
                />
              </div>
              <span className="text-[10px] text-orange-400/60">{streakState.dailyReads}/{streakState.dailyGoal}</span>
            </div>

            {/* Reputation Points */}
            {user?.reputation && user.reputation > 0 && (
              <div
                className="hidden md:flex items-center gap-1.5 rounded-xl bg-primary-500/10 px-3 py-1.5 border border-primary-500/20"
                title={`${user.reputation} reputation points`}
                aria-label={`${user.reputation} reputation points`}
              >
                <Zap className="h-4 w-4 text-primary-500" />
                <span className="text-sm font-bold text-primary-500">{user.reputation}</span>
              </div>
            )}

            {/* UI-098: Mobile Garages link. T-76: garages are members-only, so
                do not offer the link to a visitor who would only be bounced
                back to /welcome by the layout guard. */}
            {isAuthenticated && (
              <Link
                to="/garages"
                className="rounded-xl p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary lg:hidden"
                aria-label="Garages"
              >
                <Users className="h-5 w-5" />
              </Link>
            )}

            {/* T-177: theme toggle REMOVED — light was shipped with colours
                defined for dark only (pale-gray body text on white, dark
                sidebar beside a white column). The control comes back with the
                token work as its own row, whose acceptance is every surface
                readable at both themes — not the toggle merely existing. */}

            {/* T-76: a logged-out reader gets a sign-in CTA here instead of
                notification and profile controls that have nothing behind them.
                The CTA is deliberately secondary: reading is the primary act on
                a public page, signing in is the upgrade. */}
            {!isAuthenticated && (
              <div className="flex items-center gap-2">
                <Link
                  to="/auth/login"
                  className="rounded-xl px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
                >
                  Sign in
                </Link>
                <Link
                  to="/auth/register"
                  className="rounded-xl bg-primary-500 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  Join
                </Link>
              </div>
            )}

            {/* Notifications */}
            {isAuthenticated && (
            <Link
              to="/notifications"
              className="relative rounded-xl p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
            >
              <Bell className="h-5 w-5" />
              {unreadCount && unreadCount.count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-[10px] font-bold text-white ring-2 ring-bg-primary">
                  {unreadCount.count > 9 ? '9+' : unreadCount.count}
                </span>
              )}
            </Link>
            )}

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-2 rounded-xl p-1.5 transition-colors hover:bg-bg-tertiary"
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600">
                    <User className="h-4 w-4 text-white" />
                  </div>
                )}
                <ChevronDown className={`h-4 w-4 text-text-tertiary transition-transform hidden sm:block ${isProfileOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isProfileOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsProfileOpen(false)}
                  />
                  <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-border-primary bg-bg-secondary shadow-xl">
                    {/* User Info */}
                    <div className="border-b border-border-primary p-4">
                      <div className="flex items-center gap-3">
                        {user?.avatarUrl ? (
                          <img src={user.avatarUrl} alt="" className="h-12 w-12 rounded-xl object-cover" />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600">
                            <User className="h-6 w-6 text-white" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 font-semibold text-text-primary truncate">
                            {user?.displayName || user?.username}
                            {user?.isPro && <Crown className="h-3 w-3 text-amber-500 flex-shrink-0" />}
                          </div>
                          <div className="text-sm text-text-tertiary truncate">
                            @{user?.username}
                          </div>
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="mt-3 flex items-center gap-4">
                        {streak && (
                          <div className="flex items-center gap-1">
                            <Flame className="h-4 w-4 text-orange-500" />
                            <span className="text-sm font-medium text-text-primary">{streak.currentStreak}</span>
                            <span className="text-xs text-text-tertiary">streak</span>
                          </div>
                        )}
                        {user?.reputation !== undefined && (
                          <div className="flex items-center gap-1">
                            <Zap className="h-4 w-4 text-primary-500" />
                            <span className="text-sm font-medium text-text-primary">{user.reputation}</span>
                            <span className="text-xs text-text-tertiary">rep</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="p-2">
                      <Link
                        to={`/user/${user?.username}`}
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
                      >
                        View Profile
                      </Link>
                      <Link
                        to="/bookmarks"
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
                      >
                        My Bookmarks
                      </Link>
                      <Link
                        to="/invite"
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
                      >
                        Invite Friends
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setIsProfileOpen(false)}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
                      >
                        Settings
                      </Link>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-border-primary p-2">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-400 transition-colors hover:bg-red-500/10"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {streak && <StreakMilestoneToast currentStreak={streak.currentStreak} />}

      {isCommandPaletteOpen && (
        <Suspense fallback={null}>
          <CommandPalette
            isOpen={isCommandPaletteOpen}
            onClose={() => setIsCommandPaletteOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
}
